import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import * as installationApi from '@/api/installation.api';
import { separarModelos, unirModelos, type ModeloEnEdicion } from '@/lib/catalogo';
import {
  fusionarPestana,
  nombreDePestana,
  pestanaDeLaUrl,
  pestanaTieneCambios,
  type PestanaDeInstalacion,
} from '@/lib/installation-draft';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Modal } from '@/components/ui/Modal';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Toggle } from '@/components/ui/Toggle';
import { PageHeader } from '@/components/common/PageHeader';
import { CatalogoTab } from '@/components/installation/CatalogoTab';
import { MediaThumb, UploadImageButton } from '@/components/installation/MediaThumb';
import { MensajesTab } from '@/components/installation/MensajesTab';
import { InspectorTab } from '@/components/installation/InspectorTab';
import { TrabajosTab } from '@/components/installation/TrabajosTab';
import { GlosarioTab } from '@/components/installation/GlosarioTab';
import { CopilotoTab } from '@/components/installation/CopilotoTab';
import { PendientesTab } from '@/components/installation/PendientesTab';
import { useToast } from '@/hooks/useToast';

/**
 * Administración de la CONFIGURACIÓN DE LA INSTALACIÓN (solo ADMIN): datos del
 * negocio, persona/prompt del agente, catálogo con precios, reglas de CRM y
 * plantillas de mensajes. Es la contraparte visual del des-hardcodeo: lo que
 * antes vivía en código para OSITO aquí se ve y se edita como dato.
 *
 * GUARDADO POR PESTAÑA: hay un borrador local de toda la pantalla, pero cada
 * pestaña se guarda sola, con su propio botón. El PUT sigue mandando la config
 * completa (el endpoint reemplaza todo), armada con lo que hay en el servidor +
 * lo que tocó ESA pestaña — ver `lib/installation-draft.ts`. Antes había un
 * único "Guardar cambios" que mandaba las diez pestañas juntas: con eso, abrir
 * la pantalla y guardar un precio publicaba de paso cualquier cosa a medio
 * editar en las otras nueve.
 */

/** Fila editable de un centro de operación (números como texto en el borrador). */
interface CentroRowDraft {
  nombre: string;
  lat: string;
  lng: string;
  radioKm: string;
}

interface Draft {
  businessName: string;
  currencyPrefix: string;
  slaHours: string;
  notionDatabaseId: string;
  agent: {
    name: string;
    description: string;
    model: string;
    maxTokens: string;
    temperature: string;
    systemPrompt: string;
  };
  /**
   * El catálogo como UNA lista: precio y meses de garantía del mismo modelo.
   * En la base siguen siendo dos columnas; ver `lib/catalogo.ts`.
   */
  catalog: ModeloEnEdicion[];
  leadStatusLabels: Record<string, string>;
  editableFields: { key: string; label: string; placeholder: string }[];
  zonas: string;
  tiposServicio: string;
  centros: CentroRowDraft[];
  messages: installationApi.MessagesConfig;
  /** Reglas de envío (números como texto mientras se edita). */
  envio: {
    maxBurbujas: string;
    acusesLectura: boolean;
    /** Términos internos, uno por línea mientras se edita. */
    guardaActiva: boolean;
    guardaTerminos: string;
    /** Señales estructurales del discurso (tercera persona, meta-referencias). */
    guardaPatrones: string;
    canalRespuesta: boolean;
    gateIlegible: boolean;
    maxVideoMb: string;
  };
  /** Reglas de conversación: qué VE el asistente. */
  conversacion: installationApi.ConversacionConfig;
  /** Agente Inspector: el portero que corre antes de la asesora. */
  inspector: installationApi.InspectorConfig;
  /** Catálogos y reglas del módulo Trabajos (tanda EMT-CNV-2026-01). */
  trabajos: installationApi.TrabajosConfig;
  /** El vocabulario del negocio: viaja dentro del prompt del copiloto. */
  glosario: installationApi.Glosario;
  /** El copiloto interno. Nace apagado y con la identidad vacía. */
  copiloto: installationApi.CopilotoConfig;
  /** Pendientes del vendedor. Nace sin avisarle a nadie. */
  pendientes: installationApi.PendientesConfig;
  /** A6 · Tiendas, horario y reglas de reparto de Osito. */
  osito: installationApi.OsitoConfig;
}

/**
 * Etiquetas visibles de cada descripción de entrada no-texto, en el orden en
 * que se muestran. El texto lo escribe el dueño; esto solo nombra la fila.
 */
const ENTRADA_NO_TEXTO_LABELS: [keyof installationApi.EntradaNoTextoConfig, string][] = [
  ['imagen', 'Foto sin texto'],
  ['video', 'Video'],
  ['notaVoz', 'Nota de voz que no se pudo transcribir'],
  ['audio', 'Audio que no se pudo transcribir'],
  ['sticker', 'Sticker'],
  ['ubicacion', 'Ubicación'],
  ['documento', 'Documento sin nombre'],
  ['documentoConNombre', 'Documento con nombre — usa {{nombre}}'],
  ['contacto', 'Contacto compartido'],
  ['multimedia', 'Otro archivo'],
  ['noDescifrado', 'Mensaje que no se pudo leer'],
];

/**
 * Los módulos que se pueden apagar, con el nombre que ve el dueño. El orden es
 * el de la pantalla. La clave es la de la config, no la de la ruta: una sola
 * llave apaga varias pantallas (instalaciones son cinco).
 */
const MODULOS_DEL_PANEL: [keyof installationApi.ModulosActivos, string, string][] = [
  [
    'instalaciones',
    'Trabajos e instalaciones',
    'Trabajos, garantías, despachos, técnicos en la calle y su desempeño. Osito vende ropa: no instala nada.',
  ],
  ['campanas', 'Campañas', 'Envío masivo de mensajes.'],
  [
    'descartes',
    'Descartados',
    'La cola de los clientes que no contestaron. Apagarla no deja de descartar: solo deja de poder mirarlo.',
  ],
  ['entrenamiento', 'Entrenamiento', 'Correcciones al asistente a partir de chats reales.'],
];

/**
 * S-14 · Bloques del resumen diario, todos encendidos, para cuando el server
 * todavía no devuelve la config (panel viejo o primera carga).
 */
const DEFAULT_INCLUYE: installationApi.ResumenIncluye = {
  completados: true,
  noCompletados: true,
  vencidos: true,
  cierresFueraDeRadio: true,
  garantiasAbiertas: true,
  reincidencias: true,
  montoVendido: true,
  esperandoPersona: true,
};

/** Tope de burbujas por turno cuando el server aún no devuelve `envio`. */
const DEFAULT_MAX_BURBUJAS = 2;

/** Nombres de los días, indexados igual que `Date.getDay()` (0 = domingo). */
const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

/** Nombre base (es) de cada estado del lead, para no mostrar el enum crudo. */
const LEAD_STATUS_BASE_LABELS: Record<string, string> = {
  CALIENTE: 'Lead caliente',
  TIBIO: 'Lead tibio',
  FRIO: 'Lead frío',
  CERRADO: 'Cerrado (venta)',
  NO_CERRADO: 'No cerrado',
};

const csv = (list: string[] | undefined): string => (list ?? []).join(', ');
const uncsv = (text: string): string[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

function fromConfig(cfg: installationApi.InstallationConfig): Draft {
  return {
    businessName: cfg.businessName,
    currencyPrefix: cfg.currencyPrefix,
    slaHours: String(cfg.slaHours),
    notionDatabaseId: cfg.notionDatabaseId ?? '',
    agent: {
      name: cfg.agent.name,
      description: cfg.agent.description,
      model: cfg.agent.model,
      maxTokens: String(cfg.agent.maxTokens),
      temperature: String(cfg.agent.temperature),
      systemPrompt: cfg.agent.systemPrompt,
    },
    catalog: unirModelos(cfg.catalog, cfg.trabajos?.modelos ?? []),
    leadStatusLabels: { ...cfg.crm.leadStatusLabels },
    editableFields: cfg.crm.editableFields.map((f) => ({ ...f })),
    zonas: csv(cfg.crm.zonas),
    tiposServicio: csv(cfg.crm.tiposServicio),
    // `?? []` por si el server aún no conoce centros (config vieja en caché).
    centros: (cfg.crm.centros ?? []).map((c) => ({
      nombre: c.nombre,
      lat: String(c.lat),
      lng: String(c.lng),
      radioKm: String(c.radioKm),
    })),
    messages: { ...cfg.messages },
    // `?? {}` por si el server aún no devuelve `envio` (config vieja en caché).
    envio: {
      maxBurbujas: String(cfg.envio?.maxBurbujas ?? DEFAULT_MAX_BURBUJAS),
      acusesLectura: cfg.envio?.acusesLectura ?? true,
      guardaActiva: cfg.envio?.guardaSalida?.activa ?? true,
      guardaTerminos: (cfg.envio?.guardaSalida?.terminos ?? []).join('\n'),
      guardaPatrones: (cfg.envio?.guardaSalida?.patrones ?? []).join('\n'),
      canalRespuesta: cfg.envio?.canalRespuesta ?? false,
      gateIlegible: cfg.envio?.turnoIlegible?.activo ?? true,
      maxVideoMb: String(cfg.envio?.maxVideoMb ?? 16),
    },
    conversacion: {
      operador: { ...cfg.conversacion.operador },
      entradaNoTexto: { ...cfg.conversacion.entradaNoTexto },
    },
    inspector: {
      ...cfg.inspector,
      capas: { ...cfg.inspector.capas },
      indicadores: cfg.inspector.indicadores.map((i) => ({ ...i })),
      cortesia: { ...cfg.inspector.cortesia },
      tiposCaso: cfg.inspector.tiposCaso.map((t) => ({ ...t, numeroIds: [...t.numeroIds] })),
      noElegible: { ...cfg.inspector.noElegible },
    },
    trabajos: {
      // Copias defensivas: el borrador se edita fila por fila y no debe mutar
      // la respuesta cacheada de react-query.
      modelos: (cfg.trabajos?.modelos ?? []).map((m) => ({ ...m })),
      formasPago: (cfg.trabajos?.formasPago ?? []).map((f) => ({ ...f })),
      nivelesResolucion: (cfg.trabajos?.nivelesResolucion ?? []).map((n) => ({ ...n })),
      causasRaiz: (cfg.trabajos?.causasRaiz ?? []).map((c) => ({ ...c })),
      motivosNoCompletado: (cfg.trabajos?.motivosNoCompletado ?? []).map((m) => ({ ...m })),
      garantiaSlaHoras: cfg.trabajos?.garantiaSlaHoras ?? 24,
      reincidenciaVentanaDias: cfg.trabajos?.reincidenciaVentanaDias ?? null,
      geocerca: {
        radioMetrosDefault: cfg.trabajos?.geocerca?.radioMetrosDefault ?? 300,
        exigirGpsAlCerrar: cfg.trabajos?.geocerca?.exigirGpsAlCerrar ?? false,
        mapsApiKey: cfg.trabajos?.geocerca?.mapsApiKey ?? null,
      },
      encuestaGarantia: {
        activa: cfg.trabajos?.encuestaGarantia?.activa ?? false,
        preguntas: (cfg.trabajos?.encuestaGarantia?.preguntas ?? []).map((q) => ({
          ...q,
          opciones: [...(q.opciones ?? [])],
        })),
      },
      resumenDiario: {
        activo: cfg.trabajos?.resumenDiario?.activo ?? true,
        hora: cfg.trabajos?.resumenDiario?.hora ?? '20:00',
        canal: cfg.trabajos?.resumenDiario?.canal ?? 'whatsapp',
        incluye: { ...(cfg.trabajos?.resumenDiario?.incluye ?? DEFAULT_INCLUYE) },
      },
    },
    glosario: {
      // Copias defensivas, igual que en trabajos: el borrador se edita fila por
      // fila y no debe mutar la respuesta cacheada de react-query.
      terminos: (cfg.glosario?.terminos ?? []).map((t) => ({ ...t })),
      evitar: (cfg.glosario?.evitar ?? []).map((e) => ({ ...e })),
    },
    copiloto: {
      // Un panel que habla con un server viejo no recibe `copiloto`: cae a
      // APAGADO y con la identidad vacía, que es como nace.
      activo: cfg.copiloto?.activo ?? false,
      // Ante la duda, la clave del negocio: el motor que gasta de la
      // suscripción de Silva Notion nunca se enciende por omisión.
      motor: cfg.copiloto?.motor === 'cli' ? 'cli' : 'api',
      modelo: cfg.copiloto?.modelo ?? '',
      nombreAgente: cfg.copiloto?.nombreAgente ?? '',
      nombreDueno: cfg.copiloto?.nombreDueno ?? '',
      descripcionNegocio: cfg.copiloto?.descripcionNegocio ?? '',
      fraseConfirmacion: cfg.copiloto?.fraseConfirmacion ?? '',
      minutosAprobacion: cfg.copiloto?.minutosAprobacion ?? 5,
      umbralMasivo: cfg.copiloto?.umbralMasivo ?? 5,
      maxSegundosNota: cfg.copiloto?.maxSegundosNota ?? 40,
      datosRestringidos: [...(cfg.copiloto?.datosRestringidos ?? [])],
      audioSaliente: cfg.copiloto?.audioSaliente ?? false,
      vozApiKey: cfg.copiloto?.vozApiKey ?? '',
      vozId: cfg.copiloto?.vozId ?? '',
    },
    // Copia profunda: la pestaña Tiendas edita filas y no puede mutar el
    // objeto que vino del servidor (rompería "descartar cambios").
    osito: {
      ...cfg.osito,
      sucursales: (cfg.osito?.sucursales ?? []).map((x) => ({ ...x })),
      horario: (cfg.osito?.horario ?? []).map((x) => ({ ...x })),
      horarioAtencion: (cfg.osito?.horarioAtencion ?? []).map((x) => ({ ...x })),
      familiasProducto: [...(cfg.osito?.familiasProducto ?? [])],
      // Ausente = los valores con los que nace Osito, NO todo apagado: un
      // server viejo (o una respuesta a medias) dejaría el panel sin menú.
      modulos: {
        instalaciones: cfg.osito?.modulos?.instalaciones ?? false,
        campanas: cfg.osito?.modulos?.campanas ?? false,
        descartes: cfg.osito?.modulos?.descartes ?? true,
        entrenamiento: cfg.osito?.modulos?.entrenamiento ?? true,
      },
    },
    pendientes: {
      // Un panel viejo, o un server sin esta clave, cae a "no le avisa a nadie",
      // que es como nace el módulo.
      activo: cfg.pendientes?.activo ?? false,
      ventana: {
        desde: cfg.pendientes?.ventana?.desde ?? '09:00',
        hasta: cfg.pendientes?.ventana?.hasta ?? '20:00',
      },
      llamadas: {
        activo: cfg.pendientes?.llamadas?.activo ?? false,
        minutosEscalacion: cfg.pendientes?.llamadas?.minutosEscalacion ?? 30,
        minutosEscalacionVenta: cfg.pendientes?.llamadas?.minutosEscalacionVenta ?? 15,
      },
      maxChatsPorCorrida: cfg.pendientes?.maxChatsPorCorrida ?? 10,
      modelo: cfg.pendientes?.modelo ?? 'claude-opus-5',
      horasMargenVencimiento: cfg.pendientes?.horasMargenVencimiento ?? 4,
      soloChatsActivosDias: cfg.pendientes?.soloChatsActivosDias ?? 7,
    },
  };
}

function toPayload(
  d: Draft,
): Omit<installationApi.InstallationConfig, 'key' | 'logoKey' | 'catalogPdfKey' | 'isInternal'> {
  return {
    businessName: d.businessName.trim(),
    currencyPrefix: d.currencyPrefix.trim() || 'RD$',
    slaHours: Math.max(1, parseInt(d.slaHours, 10) || 2),
    notionDatabaseId: d.notionDatabaseId.trim() || null,
    osito: d.osito,
    agent: {
      name: d.agent.name.trim(),
      description: d.agent.description,
      model: d.agent.model.trim(),
      maxTokens: Math.max(1, parseInt(d.agent.maxTokens, 10) || 1024),
      temperature: Math.min(1, Math.max(0, parseFloat(d.agent.temperature) || 0.6)),
      systemPrompt: d.agent.systemPrompt,
    },
    catalog: separarModelos(d.catalog).catalog,
    crm: {
      leadStatusLabels: d.leadStatusLabels,
      editableFields: d.editableFields.filter((f) => f.key.trim()),
      zonas: uncsv(d.zonas),
      tiposServicio: uncsv(d.tiposServicio),
      // Solo filas completas y numéricamente válidas; radio inválido → 1 km.
      centros: d.centros
        .map((c) => ({
          nombre: c.nombre.trim(),
          lat: Number(c.lat),
          lng: Number(c.lng),
          radioKm: Number(c.radioKm) > 0 ? Number(c.radioKm) : 1,
        }))
        .filter(
          (c) =>
            c.nombre !== '' &&
            Number.isFinite(c.lat) &&
            Math.abs(c.lat) <= 90 &&
            Number.isFinite(c.lng) &&
            Math.abs(c.lng) <= 180,
        ),
    },
    messages: d.messages,
    envio: {
      // Acotado también en el server (coerce + zod); acá evitamos mandar basura.
      maxBurbujas: Math.min(
        6,
        Math.max(1, parseInt(d.envio.maxBurbujas, 10) || DEFAULT_MAX_BURBUJAS),
      ),
      acusesLectura: d.envio.acusesLectura,
      guardaSalida: {
        activa: d.envio.guardaActiva,
        // Una línea por término; se descartan las vacías.
        terminos: d.envio.guardaTerminos
          .split('\n')
          .map((t) => t.trim())
          .filter(Boolean),
        patrones: d.envio.guardaPatrones
          .split('\n')
          .map((t) => t.trim())
          .filter(Boolean),
      },
      canalRespuesta: d.envio.canalRespuesta,
      turnoIlegible: { activo: d.envio.gateIlegible, minProporcion: 1 },
      maxVideoMb: Math.min(64, Math.max(1, parseInt(d.envio.maxVideoMb, 10) || 16)),
    },
    conversacion: d.conversacion,
    inspector: d.inspector,
    // Se descartan las filas sin nombre (una fila a medio llenar no es un
    // catálogo) y se acota el período a un entero >= 1: el server vuelve a
    // aplicar las mismas reglas.
    trabajos: {
      // Los modelos salen del catálogo unificado, no de la pestaña Trabajos:
      // son la misma lista partida en dos columnas de la base.
      modelos: separarModelos(d.catalog).modelos,
      formasPago: d.trabajos.formasPago
        .filter((f) => f.nombre.trim() !== '')
        .map((f) => ({ id: f.id, nombre: f.nombre.trim(), activa: f.activa })),
      nivelesResolucion: d.trabajos.nivelesResolucion
        .filter((n) => n.nombre.trim() !== '')
        // Sin clave corta se usa el id: el server hace lo mismo y así el
        // trabajo nunca queda apuntando a una cadena vacía.
        .map((n) => ({
          id: n.id,
          codigo: n.codigo.trim() || n.id,
          nombre: n.nombre.trim(),
          activo: n.activo,
        })),
      causasRaiz: d.trabajos.causasRaiz
        .filter((c) => c.nombre.trim() !== '')
        .map((c) => ({ id: c.id, nombre: c.nombre.trim(), activo: c.activo })),
      motivosNoCompletado: d.trabajos.motivosNoCompletado
        .filter((m) => m.nombre.trim() !== '')
        .map((m) => ({ id: m.id, nombre: m.nombre.trim(), activo: m.activo })),
      // Un SLA inválido cae al default: un 0 dejaría TODAS las garantías
      // vencidas desde el segundo en que se abren.
      garantiaSlaHoras: Math.min(
        720,
        Math.max(1, Math.floor(Number(d.trabajos.garantiaSlaHoras)) || 24),
      ),
      // Vacío o inválido = sin límite (todo el histórico), el default del cliente.
      reincidenciaVentanaDias:
        d.trabajos.reincidenciaVentanaDias == null ||
        !Number.isFinite(Number(d.trabajos.reincidenciaVentanaDias)) ||
        Number(d.trabajos.reincidenciaVentanaDias) < 1
          ? null
          : Math.min(3650, Math.floor(Number(d.trabajos.reincidenciaVentanaDias))),
      geocerca: {
        // Un radio de 0 dejaría la geocerca sin sentido: cae al default.
        radioMetrosDefault: Math.min(
          100000,
          Math.max(1, Math.floor(Number(d.trabajos.geocerca.radioMetrosDefault)) || 300),
        ),
        exigirGpsAlCerrar: d.trabajos.geocerca.exigirGpsAlCerrar,
        mapsApiKey: d.trabajos.geocerca.mapsApiKey?.trim() || null,
      },
      resumenDiario: d.trabajos.resumenDiario,
      encuestaGarantia: d.trabajos.encuestaGarantia,
    },
    // Las filas a medio llenar se caen acá y también en el server: una palabra
    // sin explicación no le sirve al modelo, y una regla sin reemplazo tampoco.
    glosario: {
      terminos: d.glosario.terminos
        .filter((t) => t.termino.trim() !== '' && t.significa.trim() !== '')
        .map((t) => ({ ...t, termino: t.termino.trim(), significa: t.significa.trim() })),
      evitar: d.glosario.evitar
        .filter((e) => e.no.trim() !== '' && e.si.trim() !== '')
        .map((e) => ({ no: e.no.trim(), si: e.si.trim() })),
    },
    copiloto: {
      ...d.copiloto,
      nombreAgente: d.copiloto.nombreAgente.trim(),
      nombreDueno: d.copiloto.nombreDueno.trim(),
      descripcionNegocio: d.copiloto.descripcionNegocio.trim(),
      // Un umbral en 0 o vacío haría que TODO sea masivo, o que nada lo sea:
      // cada uno cae a su default en vez de guardar un número imposible.
      minutosAprobacion: Math.min(
        60,
        Math.max(1, Math.floor(Number(d.copiloto.minutosAprobacion)) || 5),
      ),
      umbralMasivo: Math.min(1000, Math.max(1, Math.floor(Number(d.copiloto.umbralMasivo)) || 5)),
      maxSegundosNota: Math.min(
        300,
        Math.max(1, Math.floor(Number(d.copiloto.maxSegundosNota)) || 40),
      ),
    },
    pendientes: {
      ...d.pendientes,
      // Cada número cae a su default si quedó vacío o inválido. Un 0 en chats
      // por vuelta dejaría el módulo mudo sin que nadie sepa por qué.
      maxChatsPorCorrida: Math.min(
        200,
        Math.max(1, Math.floor(Number(d.pendientes.maxChatsPorCorrida)) || 10),
      ),
      soloChatsActivosDias: Math.max(1, Math.floor(Number(d.pendientes.soloChatsActivosDias)) || 7),
      horasMargenVencimiento: Math.max(
        0,
        Math.floor(Number(d.pendientes.horasMargenVencimiento)) || 0,
      ),
      llamadas: {
        activo: d.pendientes.llamadas.activo,
        minutosEscalacion: Math.max(
          1,
          Math.floor(Number(d.pendientes.llamadas.minutosEscalacion)) || 30,
        ),
        minutosEscalacionVenta: Math.max(
          1,
          Math.floor(Number(d.pendientes.llamadas.minutosEscalacionVenta)) || 15,
        ),
      },
    },
  };
}

/**
 * Corrige la pestana pedida cuando pertenece a un modulo que este negocio no
 * usa. Mientras la config carga (`modulos` indefinido) NO corrige: adivinar en
 * ese instante haria saltar la pestana sola delante de quien esta mirando.
 */
function pestanaVisible(
  pestana: PestanaDeInstalacion,
  modulos: installationApi.ModulosActivos | undefined,
): PestanaDeInstalacion {
  if (pestana === 'trabajos' && modulos?.instalaciones === false) return 'tiendas';
  return pestana;
}

export function InstallationConfigPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['installation', 'config'],
    queryFn: installationApi.getInstallationConfig,
  });
  const [draft, setDraft] = useState<Draft | null>(null);

  // La pestaña vive en la dirección: F5 y los enlaces que se comparten caen
  // donde estaban. Antes siempre se volvía a "Negocio" y había que buscar.
  const [params, setParams] = useSearchParams();
  // Una pestana de un modulo apagado no se abre ni escribiendo `?tab=` a mano:
  // sin esto, el enlace viejo a Trabajos dejaba la pagina en blanco (su
  // disparador ya no existe, asi que no habia nada seleccionado).
  const pestana = pestanaVisible(pestanaDeLaUrl(params.get('tab')), draft?.osito.modulos);
  /** Pestaña a la que se quiere ir mientras la actual tiene cambios sin guardar. */
  const [pestanaPedida, setPestanaPedida] = useState<PestanaDeInstalacion | null>(null);

  useEffect(() => {
    // Solo la PRIMERA carga arma el borrador. Después no: tras guardar una
    // pestaña se refresca `data`, y volver a armar el borrador entero le
    // borraría a la persona lo que dejó a medio editar en las otras.
    setDraft((d) => (d === null && data ? fromConfig(data) : d));
  }, [data]);

  // Actualiza el borrador (no-op mientras aún no cargó).
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  // La config del servidor en el mismo formato que el borrador saneado: es
  // contra esto que se decide si una pestaña tiene cambios, y es la base sobre
  // la que se arma el PUT. Pasarla por `toPayload` evita falsos "sin guardar"
  // por diferencias de formato que no son cambios de nadie.
  const configActual = useMemo(() => (data ? toPayload(fromConfig(data)) : null), [data]);
  const borradorPayload = useMemo(() => (draft ? toPayload(draft) : null), [draft]);
  const hayCambios =
    configActual && borradorPayload
      ? pestanaTieneCambios(configActual, borradorPayload, pestana)
      : false;

  /** Cambia la pestaña de la dirección (sin ensuciar el historial del navegador). */
  function abrirPestana(destino: PestanaDeInstalacion) {
    const siguientes = new URLSearchParams(params);
    siguientes.set('tab', destino);
    setParams(siguientes, { replace: true });
  }

  /** Intento de salir de la pestaña: si hay cambios sin guardar, se avisa. */
  function intentarCambiarDePestana(valor: string) {
    const destino = pestanaDeLaUrl(valor);
    if (destino === pestana) return;
    if (hayCambios) {
      setPestanaPedida(destino);
      return;
    }
    abrirPestana(destino);
  }

  /** Tira los cambios de la pestaña actual y se va a la que se había pedido. */
  function descartarYCambiar() {
    if (!pestanaPedida) return;
    if (data) setDraft((d) => (d ? conPestanaDe(d, fromConfig(data), pestana) : d));
    abrirPestana(pestanaPedida);
    setPestanaPedida(null);
  }

  const save = useMutation({
    mutationFn: (destino: PestanaDeInstalacion) =>
      installationApi.saveInstallationConfig(
        // Se manda la config completa —el endpoint reemplaza todo— pero armada
        // con lo que hay en el servidor y solo las claves de esta pestaña.
        fusionarPestana(configActual as installationApi.InstallationConfigPayload, destino, {
          ...(borradorPayload as installationApi.InstallationConfigPayload),
        }),
      ),
    onSuccess: (guardada, destino) => {
      void qc.invalidateQueries({ queryKey: ['installation'] });
      // El servidor sanea lo que recibe (recorta, acota, empareja modelos). Se
      // baja al borrador lo que quedó guardado DE ESTA PESTAÑA, para que la
      // barra no siga diciendo "sin guardar" por un número que el server acotó.
      setDraft((d) => (d ? conPestanaDe(d, fromConfig(guardada), destino) : d));
      toast.success(`${nombreDePestana(destino)}: cambios guardados`);
    },
    onError: () => toast.error('No se pudo guardar la configuración'),
  });

  const logoUpload = useMutation({
    mutationFn: (file: File) => installationApi.uploadLogo(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installation'] });
      toast.success('Logo actualizado');
    },
    onError: () => toast.error('No se pudo subir el logo'),
  });

  if (isError) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Instalación"
          description="Configuración del cliente de esta instalación."
        />
        <div className="space-y-3 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No se pudo cargar la configuración.
          </p>
          <Button variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      </div>
    );
  }
  if (isLoading || !draft) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Instalación"
          description="Configuración del cliente de esta instalación."
        />
        <Spinner />
      </div>
    );
  }

  const modulos = draft.osito.modulos;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={`Instalación · ${draft.businessName || 'sin nombre'}`}
        helpKey="installation"
        description="Todo lo específico del cliente vive aquí como dato: negocio, agente, catálogo, CRM y mensajes. Cada pestaña se guarda por separado."
      />

      <Tabs defaultValue="negocio" value={pestana} onValueChange={intentarCambiarDePestana}>
        <TabsList>
          <TabsTrigger value="tiendas">Tiendas</TabsTrigger>
          <TabsTrigger value="negocio">Negocio</TabsTrigger>
          <TabsTrigger value="agente">Agente</TabsTrigger>
          <TabsTrigger value="catalogo">Catálogo</TabsTrigger>
          <TabsTrigger value="crm">CRM</TabsTrigger>
          <TabsTrigger value="mensajes" guia="installation.tab.mensajes">
            Mensajes
          </TabsTrigger>
          {modulos.instalaciones ? (
            <TabsTrigger value="trabajos" guia="installation.tab.trabajos">
              Trabajos
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="inspector">Inspector</TabsTrigger>
          <TabsTrigger value="glosario">Glosario</TabsTrigger>
          <TabsTrigger value="copiloto" guia="installation.tab.copiloto">
            Copiloto
          </TabsTrigger>
          <TabsTrigger value="pendientes">Pendientes</TabsTrigger>
        </TabsList>

        <TabsContent value="tiendas">
          <Card className="mb-4">
            <CardHeader>
              <CardTitle>¿Qué partes del panel usa este negocio?</CardTitle>
              <CardDescription>
                El producto trae módulos de otros rubros. Lo que se apague acá desaparece
                del menú y deja de abrirse, incluso escribiendo la dirección a mano. No es
                un permiso: no esconde datos, decide qué partes existen para este cliente.
              </CardDescription>
            </CardHeader>
            <CardBody className="grid gap-3">
              {MODULOS_DEL_PANEL.map(([clave, titulo, detalle]) => (
                <label key={clave} className="flex items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={modulos[clave]}
                    onChange={(e) =>
                      set({
                        osito: {
                          ...draft.osito,
                          modulos: { ...modulos, [clave]: e.target.checked },
                        },
                      })
                    }
                  />
                  <span>
                    <span className="font-medium">{titulo}</span>
                    <span className="block text-muted-foreground">{detalle}</span>
                  </span>
                </label>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Las tiendas</CardTitle>
              <CardDescription>
                Las tres sucursales de Osito. La dirección y el teléfono nacen vacíos a
                propósito: el asistente no puede mandarle a un cliente una dirección que
                nadie cargó. Complételos antes de conectar el número de publicidad.
              </CardDescription>
            </CardHeader>
            <CardBody className="grid gap-6">
              {draft.osito.sucursales.map((suc, i) => (
                <div key={suc.id || i} className="grid gap-3 rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-medium">{suc.nombre || 'Sucursal sin nombre'}</h4>
                    <div className="flex items-center gap-3 text-sm">
                      {suc.esSedeOperacion ? (
                        <span className="rounded bg-muted px-2 py-0.5">Sede de operación</span>
                      ) : null}
                      {suc.direccion.trim() === '' ? (
                        <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                          Falta la dirección
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Input
                      label="Nombre"
                      value={suc.nombre}
                      onChange={(e) =>
                        set({
                          osito: {
                            ...draft.osito,
                            sucursales: replaceAt(draft.osito.sucursales, i, {
                              ...suc,
                              nombre: e.target.value,
                            }),
                          },
                        })
                      }
                    />
                    <Input
                      label="Teléfono de la tienda"
                      value={suc.telefono}
                      placeholder="809…"
                      onChange={(e) =>
                        set({
                          osito: {
                            ...draft.osito,
                            sucursales: replaceAt(draft.osito.sucursales, i, {
                              ...suc,
                              telefono: e.target.value,
                            }),
                          },
                        })
                      }
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Dirección exacta"
                        value={suc.direccion}
                        placeholder="Av. de los Beisbolistas #…"
                        onChange={(e) =>
                          set({
                            osito: {
                              ...draft.osito,
                              sucursales: replaceAt(draft.osito.sucursales, i, {
                                ...suc,
                                direccion: e.target.value,
                              }),
                            },
                          })
                        }
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={suc.agenteResponde}
                        onChange={(e) =>
                          set({
                            osito: {
                              ...draft.osito,
                              sucursales: replaceAt(draft.osito.sucursales, i, {
                                ...suc,
                                agenteResponde: e.target.checked,
                              }),
                            },
                          })
                        }
                      />
                      El asistente responde por esta tienda
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={suc.activa}
                        onChange={(e) =>
                          set({
                            osito: {
                              ...draft.osito,
                              sucursales: replaceAt(draft.osito.sucursales, i, {
                                ...suc,
                                activa: e.target.checked,
                              }),
                            },
                          })
                        }
                      />
                      Activa
                    </label>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Horario de las tiendas</CardTitle>
              <CardDescription>
                A qué hora puede ir un cliente a comprar. El asistente lo usa para
                responder «¿hasta qué hora abren?» — si está mal acá, lo dice mal.
              </CardDescription>
            </CardHeader>
            <CardBody className="grid gap-4">
              <div className="grid gap-2">
                {draft.osito.horario.map((d, i) => (
                  <div key={d.dia} className="flex items-center gap-3">
                    <span className="w-24 text-sm">{DIAS_SEMANA[d.dia]}</span>
                    <Input
                      aria-label={`Abre ${DIAS_SEMANA[d.dia]}`}
                      type="time"
                      value={d.abre ?? ''}
                      onChange={(e) =>
                        set({
                          osito: {
                            ...draft.osito,
                            horario: replaceAt(draft.osito.horario, i, {
                              ...d,
                              abre: e.target.value || null,
                            }),
                          },
                        })
                      }
                    />
                    <Input
                      aria-label={`Cierra ${DIAS_SEMANA[d.dia]}`}
                      type="time"
                      value={d.cierra ?? ''}
                      onChange={(e) =>
                        set({
                          osito: {
                            ...draft.osito,
                            horario: replaceAt(draft.osito.horario, i, {
                              ...d,
                              cierra: e.target.value || null,
                            }),
                          },
                        })
                      }
                    />
                    {!d.abre || !d.cierra ? (
                      <span className="text-sm text-muted-foreground">Cerrado</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Horario de atención por WhatsApp</CardTitle>
              <CardDescription>
                A qué hora hay una PERSONA contestando. No es lo mismo que el horario de
                la tienda: la tienda puede estar abierta y el chat sin nadie. Este es el
                que decide cuándo el asistente usa la frase de abajo — el asistente
                contesta igual las 24 horas.
              </CardDescription>
            </CardHeader>
            <CardBody className="grid gap-4">
              <div className="grid gap-2">
                {draft.osito.horarioAtencion.map((d, i) => (
                  <div key={d.dia} className="flex items-center gap-3">
                    <span className="w-24 text-sm">{DIAS_SEMANA[d.dia]}</span>
                    <Input
                      aria-label={`Atienden desde ${DIAS_SEMANA[d.dia]}`}
                      type="time"
                      value={d.abre ?? ''}
                      onChange={(e) =>
                        set({
                          osito: {
                            ...draft.osito,
                            horarioAtencion: replaceAt(draft.osito.horarioAtencion, i, {
                              ...d,
                              abre: e.target.value || null,
                            }),
                          },
                        })
                      }
                    />
                    <Input
                      aria-label={`Atienden hasta ${DIAS_SEMANA[d.dia]}`}
                      type="time"
                      value={d.cierra ?? ''}
                      onChange={(e) =>
                        set({
                          osito: {
                            ...draft.osito,
                            horarioAtencion: replaceAt(draft.osito.horarioAtencion, i, {
                              ...d,
                              cierra: e.target.value || null,
                            }),
                          },
                        })
                      }
                    />
                    {!d.abre || !d.cierra ? (
                      <span className="text-sm text-muted-foreground">Nadie en el chat</span>
                    ) : null}
                  </div>
                ))}
              </div>
              <Textarea
                label="Qué dice el asistente cuando no hay nadie atendiendo"
                rows={2}
                value={draft.osito.fraseFueraDeHorario}
                onChange={(e) =>
                  set({ osito: { ...draft.osito, fraseFueraDeHorario: e.target.value } })
                }
              />
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Minutos para que el vendedor tome el chat"
                  type="number"
                  min={1}
                  value={String(draft.osito.minutosParaTomarElChat)}
                  onChange={(e) =>
                    set({
                      osito: {
                        ...draft.osito,
                        minutosParaTomarElChat: parseInt(e.target.value, 10) || 15,
                      },
                    })
                  }
                />
                <Input
                  label="Intentos máximos por cliente antes de descartar"
                  type="number"
                  min={1}
                  max={5}
                  value={String(draft.osito.maxIntentosPorAsunto)}
                  onChange={(e) =>
                    set({
                      osito: {
                        ...draft.osito,
                        maxIntentosPorAsunto: parseInt(e.target.value, 10) || 2,
                      },
                    })
                  }
                />
              </div>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="negocio">
          <Card>
            <CardHeader>
              <CardTitle>Datos del negocio</CardTitle>
              <CardDescription>Identidad comercial y reglas operativas básicas.</CardDescription>
            </CardHeader>
            <CardBody className="grid gap-4 md:grid-cols-2">
              <Input
                label="Nombre del negocio"
                value={draft.businessName}
                onChange={(e) => set({ businessName: e.target.value })}
              />
              <Input
                label="Prefijo de moneda"
                value={draft.currencyPrefix}
                onChange={(e) => set({ currencyPrefix: e.target.value })}
                placeholder="RD$"
              />
              <Input
                label="SLA del técnico en sitio (horas)"
                type="number"
                min={1}
                value={draft.slaHours}
                onChange={(e) => set({ slaHours: e.target.value })}
              />
              <Input
                label="Notion database ID (opcional)"
                value={draft.notionDatabaseId}
                onChange={(e) => set({ notionDatabaseId: e.target.value })}
                placeholder="Vacío = usar el del entorno"
              />
              <div className="md:col-span-2">
                <p className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Logo del negocio
                </p>
                <div className="flex items-center gap-3">
                  <MediaThumb mediaKey={data?.logoKey ?? null} alt="Logo del negocio" />
                  <div className="space-y-1">
                    <UploadImageButton
                      label={data?.logoKey ? 'Cambiar logo' : 'Subir logo'}
                      loading={logoUpload.isPending}
                      onFile={(f) => logoUpload.mutate(f)}
                    />
                    <p className="text-xs text-slate-400">
                      Sale en el PDF del catálogo que el asistente envía por WhatsApp.
                    </p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="agente">
          <Card>
            <CardHeader>
              <CardTitle>Persona del agente</CardTitle>
              <CardDescription>
                Identidad, modelo y prompt del agente AI de esta instalación. Ojo: el seed actualiza
                el Agent en base a esta config.
              </CardDescription>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Nombre"
                  value={draft.agent.name}
                  onChange={(e) => set({ agent: { ...draft.agent, name: e.target.value } })}
                />
                <Input
                  label="Modelo"
                  value={draft.agent.model}
                  onChange={(e) => set({ agent: { ...draft.agent, model: e.target.value } })}
                />
                <Input
                  label="Máx. tokens"
                  type="number"
                  value={draft.agent.maxTokens}
                  onChange={(e) => set({ agent: { ...draft.agent, maxTokens: e.target.value } })}
                />
                <Input
                  label="Temperatura (0–1)"
                  type="number"
                  step="0.1"
                  min={0}
                  max={1}
                  value={draft.agent.temperature}
                  onChange={(e) => set({ agent: { ...draft.agent, temperature: e.target.value } })}
                />
              </div>
              <Textarea
                label="Descripción"
                rows={2}
                value={draft.agent.description}
                onChange={(e) => set({ agent: { ...draft.agent, description: e.target.value } })}
              />
              <Textarea
                label="Instrucciones del asistente (prompt)"
                rows={18}
                className="font-mono text-xs"
                value={draft.agent.systemPrompt}
                onChange={(e) => set({ agent: { ...draft.agent, systemPrompt: e.target.value } })}
              />
            </CardBody>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Cómo envía el agente</CardTitle>
              <CardDescription>
                Reglas de salida por WhatsApp. Nada de esto toca lo que el agente dice; solo cómo lo
                manda.
              </CardDescription>
            </CardHeader>
            <CardBody className="grid gap-4 md:grid-cols-2">
              <div>
                <Input
                  label="Máximo de mensajes por respuesta"
                  type="number"
                  min={1}
                  max={6}
                  value={draft.envio.maxBurbujas}
                  onChange={(e) => set({ envio: { ...draft.envio, maxBurbujas: e.target.value } })}
                />
                <p className="mt-1 text-xs text-slate-400">
                  Si el agente escribe más párrafos que este número, los junta. Cada mensaje extra
                  desgasta la conexión de WhatsApp del cliente (es lo que causa el «Esperando el
                  mensaje» en iPhone). Recomendado: 2.
                </p>
              </div>
              <div className="md:col-span-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Marcar el chat como leído cuando el asistente responde
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-slate-400">
                      El doble check azul le dice a WhatsApp que del otro lado hay alguien
                      atendiendo (protege el número). Pero un chat leído deja de sonar en el celular
                      del equipo: si lo apagas, todo queda sin leer hasta que una persona lo abra.
                      El asistente nunca marca leído un chat pausado, en atención humana o donde no
                      llegó a responder.
                    </p>
                  </div>
                  <Toggle
                    checked={draft.envio.acusesLectura}
                    onChange={(next) => set({ envio: { ...draft.envio, acusesLectura: next } })}
                    label="Marcar como leído cuando el asistente responde"
                  />
                </div>
              </div>
              <div className="md:col-span-2 border-t border-surface-border pt-4 dark:border-slate-800">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      No dejar que se le escape lenguaje interno al cliente
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-slate-400">
                      El 1 de septiembre el asistente le escribió a una clienta su propio
                      razonamiento: «Debo registrarla como Frío y marcar requiere_humano = true».
                      Con esto encendido, esas frases se recortan antes de salir. Si de la respuesta
                      no queda nada para el cliente, no se manda nada y el chat pasa a la bandeja
                      para que lo tome una persona.
                    </p>
                  </div>
                  <Toggle
                    checked={draft.envio.guardaActiva}
                    onChange={(next) => set({ envio: { ...draft.envio, guardaActiva: next } })}
                    label="Filtrar lenguaje interno antes de enviar"
                  />
                </div>
                <Textarea
                  label="Palabras y frases que nunca deben salir (una por línea)"
                  rows={8}
                  className="mt-2 font-mono text-xs"
                  value={draft.envio.guardaTerminos}
                  onChange={(e) =>
                    set({ envio: { ...draft.envio, guardaTerminos: e.target.value } })
                  }
                />
                <p className="mt-1 text-xs text-slate-400">
                  Se compara sin distinguir mayúsculas ni acentos, y como palabra completa: «
                  Caliente » no salta dentro de «recalentada». Se quita solo la oración que la
                  contiene, no el mensaje entero.
                </p>
                <Textarea
                  label="Formas de hablar que delatan que está narrando (una por línea)"
                  rows={8}
                  className="mt-3 font-mono text-xs"
                  value={draft.envio.guardaPatrones}
                  onChange={(e) =>
                    set({ envio: { ...draft.envio, guardaPatrones: e.target.value } })
                  }
                />
                <p className="mt-1 text-xs text-slate-400">
                  La segunda fuga no usó ninguna palabra de la lista de arriba. Estas atrapan la
                  FORMA: hablar del cliente en tercera persona («el cliente», «esta conversación») o
                  narrar su propio proceso («paso al Paso 2», «aplico la excepción»). El asistente
                  siempre le habla al cliente de usted, nunca habla sobre él.
                </p>
              </div>

              <div className="md:col-span-2 border-t border-surface-border pt-4 dark:border-slate-800">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      No contestar cuando no hay nada que contestar
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-slate-400">
                      Si el cliente sólo mandó un sticker, una ubicación o mensajes que no se
                      pudieron leer, el asistente no responde: el chat pasa a la bandeja para que lo
                      tome una persona. Las dos fugas del 1 de septiembre pasaron justo en turnos
                      así.
                    </p>
                  </div>
                  <Toggle
                    checked={draft.envio.gateIlegible}
                    onChange={(next) => set({ envio: { ...draft.envio, gateIlegible: next } })}
                    label="No contestar turnos sin texto"
                  />
                </div>
              </div>

              <div className="md:col-span-2 border-t border-surface-border pt-4 dark:border-slate-800">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Canal exclusivo de respuesta{' '}
                      <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                        probar antes
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-slate-400">
                      El arreglo de fondo: el asistente escribe lo que va al cliente en un campo
                      aparte, y todo lo que escriba fuera de ahí no se envía nunca. Elimina el
                      problema de raíz en vez de filtrarlo. Cambia la forma de cada respuesta:
                      pruébalo en una conversación real antes de dejarlo encendido.
                    </p>
                  </div>
                  <Toggle
                    checked={draft.envio.canalRespuesta}
                    onChange={(next) => set({ envio: { ...draft.envio, canalRespuesta: next } })}
                    label="Canal exclusivo de respuesta"
                  />
                </div>
              </div>

              <div className="md:col-span-2 border-t border-surface-border pt-4 dark:border-slate-800">
                <Input
                  label="Tamaño máximo del video que se guarda (MB)"
                  type="number"
                  min={1}
                  max={64}
                  className="max-w-xs"
                  value={draft.envio.maxVideoMb}
                  onChange={(e) => set({ envio: { ...draft.envio, maxVideoMb: e.target.value } })}
                />
                <p className="mt-1 text-xs text-slate-400">
                  Los videos que te mandan los clientes ahora se guardan y puedes verlos en el chat.
                  Por encima de este tamaño el mensaje igual aparece, pero sin el archivo (los
                  videos ocupan mucho disco en el servidor). El asistente no mira los videos; son
                  para ti.
                </p>
              </div>
            </CardBody>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Qué ve el asistente</CardTitle>
              <CardDescription>
                Cómo se le cuenta al asistente lo que pasó en el chat. No cambia lo que dice; cambia
                lo que entiende. El cliente nunca ve estos textos.
              </CardDescription>
            </CardHeader>
            <CardBody className="space-y-5">
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Distinguir al operador del asistente
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-slate-400">
                      Sin esto, el asistente cree que las frases que escribió una persona del equipo
                      las dijo él, y termina contestándole al operador en vez de al cliente. Apágalo
                      solo si notas que empeora las respuestas.
                    </p>
                  </div>
                  <Toggle
                    checked={draft.conversacion.operador.distinguir}
                    onChange={(next) =>
                      set({
                        conversacion: {
                          ...draft.conversacion,
                          operador: { ...draft.conversacion.operador, distinguir: next },
                        },
                      })
                    }
                    label="Distinguir al operador del asistente"
                  />
                </div>
                <Textarea
                  label="Nota que precede a lo que escribió una persona del equipo"
                  rows={2}
                  value={draft.conversacion.operador.etiqueta}
                  onChange={(e) =>
                    set({
                      conversacion: {
                        ...draft.conversacion,
                        operador: { ...draft.conversacion.operador, etiqueta: e.target.value },
                      },
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    Cuando el cliente manda algo que no es texto
                  </p>
                  <p className="mt-0.5 text-xs leading-snug text-slate-400">
                    Antes el asistente recibía la etiqueta cruda («[Imagen]») y la leía como una
                    acotación, no como un hecho. Estos son los textos que recibe en su lugar.
                  </p>
                </div>
                <div className="space-y-2">
                  {ENTRADA_NO_TEXTO_LABELS.map(([clave, label]) => (
                    <Input
                      key={clave}
                      label={label}
                      value={draft.conversacion.entradaNoTexto[clave]}
                      onChange={(e) =>
                        set({
                          conversacion: {
                            ...draft.conversacion,
                            entradaNoTexto: {
                              ...draft.conversacion.entradaNoTexto,
                              [clave]: e.target.value,
                            },
                          },
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="catalogo">
          {/* Una tarjeta por modelo y su ficha aparte, en vez de una rejilla de
              noventa casillas. Incluye los meses de garantía, que vivían en la
              pestaña Trabajos: para el dueño siempre fueron el mismo modelo. */}
          <CatalogoTab
            filas={draft.catalog}
            onChange={(catalog) => set({ catalog })}
            currencyPrefix={draft.currencyPrefix}
            catalogPdfKey={data?.catalogPdfKey ?? null}
          />
        </TabsContent>

        <TabsContent value="crm">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Etiquetas de temperatura</CardTitle>
                <CardDescription>
                  Cómo se llama cada estado del lead en el panel y en Notion.
                </CardDescription>
              </CardHeader>
              <CardBody className="grid gap-4 md:grid-cols-3">
                {Object.entries(draft.leadStatusLabels).map(([key, label]) => (
                  <Input
                    key={key}
                    label={LEAD_STATUS_BASE_LABELS[key] ?? key}
                    value={label}
                    onChange={(e) =>
                      set({
                        leadStatusLabels: { ...draft.leadStatusLabels, [key]: e.target.value },
                      })
                    }
                  />
                ))}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Campos editables de la ficha</CardTitle>
                <CardDescription>
                  Datos del lead que un humano corrige a mano en la ficha. La clave debe coincidir
                  con la que captura el asistente (p. ej. zona, articulo_interes).
                </CardDescription>
              </CardHeader>
              <CardBody className="space-y-3">
                {draft.editableFields.map((f, i) => (
                  <div key={i} className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                    <Input
                      placeholder="clave (ej. zona)"
                      value={f.key}
                      onChange={(e) =>
                        set({
                          editableFields: replaceAt(draft.editableFields, i, {
                            ...f,
                            key: e.target.value,
                          }),
                        })
                      }
                    />
                    <Input
                      placeholder="Etiqueta"
                      value={f.label}
                      onChange={(e) =>
                        set({
                          editableFields: replaceAt(draft.editableFields, i, {
                            ...f,
                            label: e.target.value,
                          }),
                        })
                      }
                    />
                    <Input
                      placeholder="Placeholder"
                      value={f.placeholder}
                      onChange={(e) =>
                        set({
                          editableFields: replaceAt(draft.editableFields, i, {
                            ...f,
                            placeholder: e.target.value,
                          }),
                        })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Quitar campo"
                      onClick={() =>
                        set({ editableFields: draft.editableFields.filter((_, j) => j !== i) })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  onClick={() =>
                    set({
                      editableFields: [
                        ...draft.editableFields,
                        { key: '', label: '', placeholder: '' },
                      ],
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> Añadir campo
                </Button>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Zonas y tipos de servicio</CardTitle>
                <CardDescription>
                  Opciones de los selects (separadas por coma). Se usan también en Notion.
                </CardDescription>
              </CardHeader>
              <CardBody className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Zonas"
                  value={draft.zonas}
                  onChange={(e) => set({ zonas: e.target.value })}
                />
                <Input
                  label="Tipos de servicio"
                  value={draft.tiposServicio}
                  onChange={(e) => set({ tiposServicio: e.target.value })}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Centros de operación</CardTitle>
                <CardDescription>
                  Bases del negocio (ej. Santo Domingo, Punta Cana). Se muestran en el mapa de
                  Tracking y se marca si cada técnico está dentro o fuera de una base, con la
                  distancia a la más cercana.
                </CardDescription>
              </CardHeader>
              <CardBody className="space-y-3">
                {draft.centros.map((c, i) => (
                  <div key={i} className="grid gap-2 md:grid-cols-[1.4fr_1fr_1fr_0.7fr_auto]">
                    <Input
                      placeholder="Nombre (ej. Santo Domingo)"
                      value={c.nombre}
                      onChange={(e) =>
                        set({
                          centros: replaceAt(draft.centros, i, { ...c, nombre: e.target.value }),
                        })
                      }
                    />
                    <Input
                      placeholder="Latitud (ej. 18.4861)"
                      value={c.lat}
                      onChange={(e) =>
                        set({ centros: replaceAt(draft.centros, i, { ...c, lat: e.target.value }) })
                      }
                    />
                    <Input
                      placeholder="Longitud (ej. -69.9312)"
                      value={c.lng}
                      onChange={(e) =>
                        set({ centros: replaceAt(draft.centros, i, { ...c, lng: e.target.value }) })
                      }
                    />
                    <Input
                      placeholder="Radio km"
                      value={c.radioKm}
                      onChange={(e) =>
                        set({
                          centros: replaceAt(draft.centros, i, { ...c, radioKm: e.target.value }),
                        })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Quitar centro"
                      onClick={() => set({ centros: draft.centros.filter((_, j) => j !== i) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  onClick={() =>
                    set({
                      centros: [...draft.centros, { nombre: '', lat: '', lng: '', radioKm: '5' }],
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> Añadir centro
                </Button>
              </CardBody>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="mensajes">
          {/* Rediseño (feature/rediseno-config): tarjetas por plantilla con su
              switch del ruteo integrado + números/destinos al final. Los textos
              viajan con el «Guardar Mensajes» de la pestaña; el ruteo, con su
              propio botón. */}
          <MensajesTab
            messages={draft.messages}
            onChange={(patch) => set({ messages: { ...draft.messages, ...patch } })}
          />
        </TabsContent>

        <TabsContent value="glosario">
          <GlosarioTab
            value={draft.glosario}
            onChange={(patch) => set({ glosario: { ...draft.glosario, ...patch } })}
          />
        </TabsContent>

        <TabsContent value="copiloto">
          <CopilotoTab
            value={draft.copiloto}
            onChange={(patch) => set({ copiloto: { ...draft.copiloto, ...patch } })}
            isInternal={data?.isInternal === true}
          />
        </TabsContent>

        <TabsContent value="pendientes">
          <PendientesTab
            value={draft.pendientes}
            onChange={(patch) => set({ pendientes: { ...draft.pendientes, ...patch } })}
          />
        </TabsContent>

        <TabsContent value="trabajos">
          {/* Tanda EMT-CNV-2026-01: catálogos y reglas del módulo Trabajos.
              Viajan con el «Guardar Trabajos» de la pestaña. */}
          <TrabajosTab
            value={draft.trabajos}
            onChange={(patch) => set({ trabajos: { ...draft.trabajos, ...patch } })}
            // Los modelos salen del catálogo unificado: acá solo se miran.
            modelos={separarModelos(draft.catalog).modelos}
            onEditarEnCatalogo={() => intentarCambiarDePestana('catalogo')}
          />
        </TabsContent>

        <TabsContent value="inspector">
          {/* Agente Inspector (tanda 6): el portero que corre ANTES de la
              asesora. Viaja con el «Guardar Inspector» de la pestaña. */}
          <InspectorTab
            value={draft.inspector}
            onChange={(patch) => set({ inspector: { ...draft.inspector, ...patch } })}
          />
        </TabsContent>
      </Tabs>

      {/* Guardado de ESTA pestaña. Aparece solo con cambios pendientes, igual
          que la barra del ruteo en Mensajes. Se dibuja una sola vez porque solo
          hay una pestaña visible a la vez. */}
      {hayCambios && (
        <div className="sticky bottom-4 z-10 flex justify-end">
          <div className="flex items-center gap-3 rounded-full bg-white px-4 py-2 shadow-lg ring-1 ring-surface-border dark:bg-slate-900 dark:ring-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Cambios sin guardar en esta pestaña
            </span>
            <Button
              data-guia="installation.guardar"
              size="sm"
              onClick={() => save.mutate(pestana)}
              loading={save.isPending}
            >
              Guardar {nombreDePestana(pestana)}
            </Button>
          </div>
        </div>
      )}

      {/* Irse de una pestaña con cambios sin guardar es la forma más fácil de
          perder trabajo: se pregunta antes, nombrando la pestaña. */}
      <Modal
        open={pestanaPedida !== null}
        onClose={() => setPestanaPedida(null)}
        title={`Tienes cambios sin guardar en ${nombreDePestana(pestana)}`}
        description="Si sales ahora, esos cambios se pierden. Los de las otras pestañas no se tocan."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={descartarYCambiar}>
              Descartar
            </Button>
            <Button onClick={() => setPestanaPedida(null)}>Seguir editando</Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          También puedes guardar {nombreDePestana(pestana)} con el botón de abajo y volver a
          intentarlo.
        </p>
      </Modal>
    </div>
  );
}

/**
 * Devuelve el borrador con las claves de UNA pestaña traídas de `origen` (la
 * config del servidor). Sirve para las dos operaciones que existen sobre una
 * sola pestaña: descartar sus cambios y bajarle lo que el servidor guardó.
 *
 * El mapeo es distinto al del payload porque el borrador aplana el CRM en
 * campos sueltos y guarda los números como texto mientras se escriben.
 */
function conPestanaDe(borrador: Draft, origen: Draft, pestana: PestanaDeInstalacion): Draft {
  switch (pestana) {
    case 'negocio':
      return {
        ...borrador,
        businessName: origen.businessName,
        currencyPrefix: origen.currencyPrefix,
        slaHours: origen.slaHours,
        notionDatabaseId: origen.notionDatabaseId,
      };
    case 'tiendas':
      return { ...borrador, osito: origen.osito };
    case 'agente':
      return {
        ...borrador,
        agent: origen.agent,
        envio: origen.envio,
        conversacion: origen.conversacion,
      };
    case 'catalogo':
      // `catalog` del borrador YA trae los meses de garantía de cada modelo:
      // es una sola lista en pantalla aunque se guarde en dos lugares.
      return { ...borrador, catalog: origen.catalog };
    case 'crm':
      return {
        ...borrador,
        leadStatusLabels: origen.leadStatusLabels,
        editableFields: origen.editableFields,
        zonas: origen.zonas,
        tiposServicio: origen.tiposServicio,
        centros: origen.centros,
      };
    case 'mensajes':
      return { ...borrador, messages: origen.messages };
    case 'trabajos':
      // `trabajos.modelos` del borrador es solo la copia que muestra la pestaña:
      // lo que se guarda sale del catálogo unificado (ver `toPayload`).
      return { ...borrador, trabajos: origen.trabajos };
    case 'inspector':
      return { ...borrador, inspector: origen.inspector };
    case 'glosario':
      return { ...borrador, glosario: origen.glosario };
    case 'copiloto':
      return { ...borrador, copiloto: origen.copiloto };
    case 'pendientes':
      return { ...borrador, pendientes: origen.pendientes };
  }
}

/** Reemplaza el elemento `i` de una lista (inmutable). */
function replaceAt<T>(list: T[], i: number, item: T): T[] {
  return list.map((x, j) => (j === i ? item : x));
}
